export type ImportAction = 'created' | 'updated' | 'error';

export interface ImportEvent {
    row: number;
    code: string;
    action: ImportAction;
    error?: string;
}

export interface ImportSummary {
    totalRows: number;
    created: number;
    updated: number;
    errors: number;
}

interface ImportHandlers {
    onRow: (e: ImportEvent) => void;
    onDone: (summary: ImportSummary) => void;
    onError: (err: unknown) => void;
    signal?: AbortSignal;
}

export async function importSwiftFlowErrorsCsv(
    csvString: string,
    token: string | null,
    { onRow, onDone, onError, signal }: ImportHandlers,
) {
    try {
        const base = import.meta.env.BASE_URL || '/';
        const res = await fetch(`${base}api/swift-flow-errors/import`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: JSON.stringify({ csvString }),
            signal,
        });

        if (!res.ok || !res.body) {
            throw new Error(`Import failed with status ${res.status}`);
        }

        const reader = res.body.getReader();
        const decoder = new TextDecoder('utf-8');
        let buffer = '';

        while (true) {
            const { value, done } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });

            let idx: number;
            while ((idx = buffer.indexOf('\n\n')) >= 0) {
                const rawChunk = buffer.slice(0, idx).trim();
                buffer = buffer.slice(idx + 2);

                if (!rawChunk) continue;

                const lines = rawChunk.split('\n');
                const eventLine = lines.find((l) => l.startsWith('event:'));
                const dataLine = lines.find((l) => l.startsWith('data:'));
                if (!dataLine) continue;

                const jsonStr = dataLine.replace(/^data:\s*/, '');

                try {
                    const payload = JSON.parse(jsonStr);
                    if (eventLine && eventLine.includes('done')) {
                        onDone(payload as ImportSummary);
                    } else {
                        onRow(payload as ImportEvent);
                    }
                } catch (err) {
                    // eslint-disable-next-line no-console
                    console.error('Failed to parse SSE chunk', err, rawChunk);
                }
            }
        }
    } catch (err) {
        onError(err);
    }
}
